import { useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuthStore } from '@/stores/useAuthStore'

interface RequireAuthProps {
  children: React.ReactNode
}

export function RequireAuth({ children }: RequireAuthProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const { user, loading, initialized, initialize } = useAuthStore()

  // Initialize auth on mount
  useEffect(() => {
    initialize()
  }, [initialize])

  // Redirect to login if not authenticated. Preserve `location.search`
  // so query-param-driven entry points survive the auth bounce — the
  // canonical case is the calendar wrap-up link
  // (`/concierge?mode=wrapup&block=…`) clicked from Apple/iOS/Google
  // Calendar by a logged-out user. Dropping the search wiped those
  // params and landed the user on the default concierge page after
  // login instead of the wrap-up modal for the block they tapped.
  useEffect(() => {
    if (initialized && !loading && !user) {
      const returnTo = encodeURIComponent(
        location.pathname + location.search,
      )
      navigate(`/login?returnTo=${returnTo}`, { replace: true })
    }
  }, [initialized, loading, user, navigate, location.pathname, location.search])

  // Loading state
  if (!initialized || loading) {
    return (
      <div className="h-dvh flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="w-6 h-6 border border-foreground/20 border-t-foreground rounded-full animate-spin mx-auto" />
          <p className="mt-4 text-sm text-muted-foreground font-light">
            Loading...
          </p>
        </div>
      </div>
    )
  }

  // Not authenticated
  if (!user) {
    return null
  }

  return <>{children}</>
}
